import {BaseApi} from './BaseApi'

// =============================================
// Dashboard KPI Types
// =============================================

export interface DashboardKPI {
  label: string
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
  format?: 'number' | 'percent' | 'currency' | 'hours'
}

export interface DashboardSummaryResponse {
  periodStart: string
  periodEnd: string
  kpis: {
    totalWorkersFilled: DashboardKPI
    totalHoursWorked: DashboardKPI
    totalEarnings: DashboardKPI
    fillRatio: DashboardKPI
    qualityFillRatio: DashboardKPI
    noShowRate: DashboardKPI
    activeOrders: DashboardKPI
    activeAgencies: DashboardKPI
  }
  trends: TrendDataPoint[]
}

export interface TrendDataPoint {
  date: string
  fills: number
  noShows: number
  earnings: number
  hours: number
}

// =============================================
// Earnings Types
// =============================================

export interface EarningsSummaryResponse {
  periodStart: string
  periodEnd: string
  totalEarnings: number
  totalHours: number
  averageEarningsPerWorker: number
  breakdown: EarningsBreakdown[]
}

export interface EarningsBreakdown {
  period: string
  earnings: number
  hoursWorked: number
  workerCount: number
}

// =============================================
// Agency Metrics Types
// =============================================

export interface AgencyRankingResponse {
  rank: number
  agencyId: string
  agencyName?: string
  compositeScore: number
  fillRatio: number
  qualityFillRatio: number
  noShowRate: number
  avgResponseTimeMs: number
  totalFills: number
  totalOrders: number
}

export interface AgencyReliabilityScore {
  agencyId: string
  calculatedAt: string
  periodStart: string
  periodEnd: string
  fillRatioScore: number
  qualityFillScore: number
  noShowRateScore: number
  overPledgeRateScore: number
  responseTimeScore: number
  compositeScore: number
  metrics?: AgencyReliabilityMetrics
}

export interface AgencyReliabilityMetrics {
  totalOrders: number
  totalFilled: number
  totalRequested: number
  totalNoShows: number
  totalQualityFills: number
  avgResponseTimeMs: number
  overPledgeCount: number
  totalReorders: number
  fillRatio: number
  qualityFillRatio: number
  noShowRate: number
}

// =============================================
// Recruiter Metrics Types
// =============================================

export interface RecruiterLeaderboardEntry {
  rank: number
  userId: string
  userName: string
  agencyId: string
  agencyName?: string
  totalFills: number
  totalPoints: number
  fillsPerHour: number
  avgCallToFillMinutes: number
}

export interface RecruiterMetrics {
  userId: string
  userName: string
  agencyId: string
  periodStart: string
  periodEnd: string
  totalFills: number
  totalCalls: number
  avgCallToFillMs: number
  fillsPerHour: number
  qualityFillRatio: number
  totalPoints: number
  pointsBreakdown?: PointsBreakdown
}

export interface PointsBreakdown {
  fillPoints: number
  qualityBonusPoints: number
  velocityBonusPoints: number
  noShowPenaltyPoints: number
}

// =============================================
// Company Retention Types
// =============================================

export interface CompanyRetentionResponse {
  periodStart: string
  periodEnd: string
  totalCompanies: number
  activeCompanies: number
  retentionRate: number
  newCompanies: number
  churnedCompanies: number
  topCompaniesByOrders: CompanyMetric[]
  atRiskCompanies: AtRiskCompany[]
}

export interface CompanyMetric {
  companyId: string
  companyName?: string
  orderCount: number
  fillSuccessRate: number
  totalSpend: number
}

export interface AtRiskCompany {
  companyId: string
  companyName?: string
  lastOrderDate: string
  orderDeclinePercent: number
  riskScore: number
}

// =============================================
// Fill Metrics Types
// =============================================

export interface FillRatioResult {
  requestedCount: number
  filledCount: number
  fillRatio: number
  byAgency?: AgencyFillBreakdown[]
}

export interface AgencyFillBreakdown {
  agencyId: string
  agencyName?: string
  requested: number
  filled: number
  fillRatio: number
}

export interface QualityFillRatioResult {
  requestedCount: number
  filledCount: number
  qualityFillCount: number
  noShowCount: number
  qualityFillRatio: number
  byAgency?: AgencyQualityBreakdown[]
}

export interface AgencyQualityBreakdown {
  agencyId: string
  agencyName?: string
  filled: number
  qualityFills: number
  noShows: number
  qualityFillRatio: number
}

export interface FillVelocityResult {
  periodStart: string
  periodEnd: string
  totalFills: number
  hoursInPeriod: number
  fillsPerHour: number
  targetBenchmark: number
  meetsBenchmark: boolean
}

// =============================================
// Attendance Types
// =============================================

export interface WorkerSuspensionStatus {
  suspended: boolean
}

// =============================================
// Company-Specific Metrics Types
// =============================================

export interface CompanyMetricsKPI {
  label: string
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
  format?: 'number' | 'percent' | 'currency' | 'hours'
}

export interface CompanyMetricsOverviewResponse {
  companyId: string
  companyName: string
  periodStart: string
  periodEnd: string
  totalSpendYTD: CompanyMetricsKPI
  totalSpendMTD: CompanyMetricsKPI
  activeWorkers: CompanyMetricsKPI
  ordersThisMonth: CompanyMetricsKPI
  fillRate: CompanyMetricsKPI
  qualityFillRate: CompanyMetricsKPI
  noShowRate: CompanyMetricsKPI
  avgTimeToFill: CompanyMetricsKPI
  totalHoursWorkedMTD: CompanyMetricsKPI
  accountHealthScore: CompanyMetricsKPI
}

export interface InvoiceBreakdown {
  period: string
  totalBill: number
  totalPay: number
  margin: number
  invoiceCount: number
}

export interface DepartmentSpend {
  departmentId: string
  departmentName: string
  totalSpend: number
  percentage: number
}

export interface PositionSpend {
  positionId: string
  positionName: string
  totalSpend: number
  hoursWorked: number
  avgRate: number
}

export interface CompanyFinancialMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  totalBilledAmount: number
  totalPayAmount: number
  grossMargin: number
  averageMarkupPercent: number
  averageInvoiceSize: number
  totalInvoices: number
  pendingInvoices: number
  approvedInvoices: number
  outstandingBalance: number
  monthlyBreakdown: InvoiceBreakdown[]
  spendByDepartment: DepartmentSpend[]
  spendByPosition: PositionSpend[]
}

export interface AgencyPerformanceForCompany {
  agencyId: string
  agencyName: string
  fillRate: number
  qualityFillRate: number
  noShowRate: number
  avgResponseTimeMinutes: number
  totalFills: number
  totalOrders: number
  reliabilityScore: number
}

export interface OrderStatusBreakdown {
  status: string
  count: number
  percentage: number
}

export interface FillRateTrend {
  date: string
  fillRate: number
  qualityFillRate: number
  ordersCount: number
  fillsCount: number
}

export interface CompanyOrdersMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  totalOrders: number
  ordersThisPeriod: number
  averageOrderSize: number
  orderCompletionRate: number
  cancelledOrders: number
  fillRate: number
  qualityFillRate: number
  avgFillTimeHours: number
  sameDayFillRate: number
  reorderRate: number
  ordersByStatus: OrderStatusBreakdown[]
  agencyPerformance: AgencyPerformanceForCompany[]
  fillRateTrend: FillRateTrend[]
}

export interface TopWorker {
  workerId: string
  workerName: string
  agencyId: string
  agencyName: string
  totalEarnings: number
  totalHours: number
  attendanceRate: number
  tenure: number
  positionsWorked: number
}

export interface PositionAnalytics {
  positionId: string
  positionName: string
  orderCount: number
  fillRate: number
  avgPayRate: number
  avgFillTimeHours: number
  totalHoursWorked: number
}

export interface HoursBreakdown {
  date: string
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  totalHours: number
}

export interface CompanyWorkforceMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  totalUniqueWorkers: number
  activeWorkers: number
  newWorkersThisMonth: number
  workerTurnoverRate: number
  averageTenureDays: number
  retentionRate: number
  totalHoursWorked: number
  regularHours: number
  overtimeHours: number
  doubleTimeHours: number
  overtimePercentage: number
  attendanceRate: number
  noShowCount: number
  noShowRate: number
  topEarners: TopWorker[]
  mostHoursWorked: TopWorker[]
  bestAttendance: TopWorker[]
  positionAnalytics: PositionAnalytics[]
  hoursTrend: HoursBreakdown[]
}

export interface PeriodComparison {
  metric: string
  currentValue: number
  previousValue: number
  changePercent: number
  trend: 'up' | 'down' | 'stable'
}

export interface MonthlyTrend {
  month: string
  spend: number
  orders: number
  fills: number
  workers: number
  hours: number
  fillRate: number
  noShowRate: number
}

export interface CompanyTrendsMetricsResponse {
  companyId: string
  periodStart: string
  periodEnd: string
  spendGrowthRate: number
  orderVolumeGrowth: number
  fillRateChange: number
  workforceGrowth: number
  periodComparisons: PeriodComparison[]
  monthlyTrends: MonthlyTrend[]
  peakMonth?: string
  lowMonth?: string
  projectedNextMonthSpend?: number
  churnRiskScore?: number
}

export interface CompanyMetricsAccessResponse {
  canAccess: boolean
  agencyIds: string[] | null
}

// =============================================
// Date Range Helper
// =============================================

export interface DateRange {
  startDate: Date
  endDate: Date
}

// =============================================
// Agency Dashboard Types
// =============================================

export interface AgencyKPI {
  label: string
  value: number
  previousValue?: number
  changePercent?: number
  trend?: 'up' | 'down' | 'stable'
  format?: 'number' | 'percent' | 'currency' | 'hours' | 'time'
}

export interface AgencyDashboardSummaryResponse {
  agencyId: string
  agencyName?: string
  periodStart: string
  periodEnd: string
  kpis: {
    totalFills: AgencyKPI
    totalHoursWorked: AgencyKPI
    fillRatio: AgencyKPI
    qualityFillRatio: AgencyKPI
    noShowRate: AgencyKPI
    avgResponseTime: AgencyKPI
    activeOrders: AgencyKPI
    reliabilityScore: AgencyKPI
  }
  scoreBreakdown: {
    fillRatioScore: number
    qualityFillScore: number
    noShowRateScore: number
    overPledgeRateScore: number
    responseTimeScore: number
  }
  trends: {date: string; fills: number; noShows: number; hours: number}[]
  scoreHistory: {date: string; score: number}[]
}

export interface AgencyRecruiterEntry {
  rank: number
  userId: string
  userName: string
  totalFills: number
  totalPoints: number
  fillRatio: number
  qualityRatio: number
  avgCallToFillMinutes: number
  fillsPerHour: number
}

export interface AgencyRecruiterResponse {
  agencyId: string
  periodStart: string
  periodEnd: string
  totalRecruiters: number
  leaderboard: AgencyRecruiterEntry[]
  aggregateMetrics: {
    totalCalls: number
    totalCandidatesReached: number
    totalCandidatesAccepted: number
    totalQualityFills: number
    totalNoShows: number
    avgFillsPerRecruiter: number
    avgPointsPerRecruiter: number
  }
}

export interface AgencyClientEntry {
  companyId: string
  companyName?: string
  orderCount: number
  fillCount: number
  fillSuccessRate: number
  hoursWorked: number
  avgFillTimeMinutes: number
}

export interface AgencyAtRiskClient {
  companyId: string
  companyName?: string
  lastOrderDate: string
  orderDeclinePercent: number
  riskScore: number
}

export interface AgencyClientResponse {
  agencyId: string
  periodStart: string
  periodEnd: string
  totalClients: number
  activeClients: number
  topClients: AgencyClientEntry[]
  atRiskClients: AgencyAtRiskClient[]
}

export interface AgencyTrendsResponse {
  agencyId: string
  periodStart: string
  periodEnd: string
  fillRateTrend: {date: string; value: number}[]
  noShowRateTrend: {date: string; value: number}[]
  scoreTrend: {date: string; value: number}[]
  orderVolumeTrend: {date: string; value: number}[]
  hoursTrend: {date: string; value: number}[]
  periodComparison: {
    metric: string
    currentPeriod: number
    previousPeriod: number
    changePercent: number
  }[]
}

export type GroupByPeriod = 'month' | 'quarter' | 'year'

/**
 * Metrics API service for MSP Platform Admin Dashboard
 * Provides access to all metrics and analytics endpoints
 */
class MetricsApi extends BaseApi {
  // =============================================
  // Dashboard Endpoints
  // =============================================

  /**
   * Get comprehensive dashboard summary with all KPIs
   * GET /api/metrics/dashboard/summary
   */
  async getDashboardSummary(startDate: Date, endDate: Date): Promise<DashboardSummaryResponse> {
    try {
      const response = await this.get<DashboardSummaryResponse>('/api/metrics/dashboard/summary', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching dashboard summary:', error)
      throw error
    }
  }

  /**
   * Get earnings summary with period breakdown
   * GET /api/metrics/earnings/summary
   */
  async getEarningsSummary(
    startDate: Date,
    endDate: Date,
    groupBy: GroupByPeriod = 'month',
  ): Promise<EarningsSummaryResponse> {
    try {
      const response = await this.get<EarningsSummaryResponse>('/api/metrics/earnings/summary', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          groupBy,
        },
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching earnings summary:', error)
      throw error
    }
  }

  // =============================================
  // Agency Performance Endpoints
  // =============================================

  /**
   * Get all agencies ranked by composite score
   * GET /api/metrics/agencies/rankings
   */
  async getAgencyRankings(
    startDate: Date,
    endDate: Date,
    limit: number = 20,
  ): Promise<AgencyRankingResponse[]> {
    try {
      const response = await this.get<AgencyRankingResponse[]>('/api/metrics/agencies/rankings', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit,
        },
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency rankings:', error)
      throw error
    }
  }

  /**
   * Get latest reliability score for an agency
   * GET /api/metrics/agency-score/:agencyId
   */
  async getAgencyScore(agencyId: string): Promise<AgencyReliabilityScore | null> {
    try {
      const response = await this.get<AgencyReliabilityScore | null>(
        `/api/metrics/agency-score/${agencyId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency score:', agencyId, error)
      throw error
    }
  }

  /**
   * Get agency score history
   * GET /api/metrics/agency-score/:agencyId/history
   */
  async getAgencyScoreHistory(
    agencyId: string,
    limit: number = 12,
  ): Promise<AgencyReliabilityScore[]> {
    try {
      const response = await this.get<AgencyReliabilityScore[]>(
        `/api/metrics/agency-score/${agencyId}/history`,
        {
          params: {limit},
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency score history:', agencyId, error)
      throw error
    }
  }

  /**
   * Get agency leaderboard for a period
   * GET /api/metrics/agency-leaderboard
   */
  async getAgencyLeaderboard(
    startDate: Date,
    endDate: Date,
    limit: number = 10,
  ): Promise<AgencyReliabilityScore[]> {
    try {
      const response = await this.get<AgencyReliabilityScore[]>('/api/metrics/agency-leaderboard', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit,
        },
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency leaderboard:', error)
      throw error
    }
  }

  // =============================================
  // Recruiter Performance Endpoints
  // =============================================

  /**
   * Get global recruiter leaderboard across all agencies
   * GET /api/metrics/recruiters/global-leaderboard
   */
  async getGlobalRecruiterLeaderboard(
    startDate: Date,
    endDate: Date,
    limit: number = 20,
  ): Promise<RecruiterLeaderboardEntry[]> {
    try {
      const response = await this.get<RecruiterLeaderboardEntry[]>(
        '/api/metrics/recruiters/global-leaderboard',
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit,
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching global recruiter leaderboard:', error)
      throw error
    }
  }

  /**
   * Get recruiter leaderboard by agency
   * GET /api/metrics/recruiter-leaderboard/:agencyId
   */
  async getRecruiterLeaderboardByAgency(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    limit: number = 10,
  ): Promise<RecruiterMetrics[]> {
    try {
      const response = await this.get<RecruiterMetrics[]>(
        `/api/metrics/recruiter-leaderboard/${agencyId}`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit,
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching recruiter leaderboard:', agencyId, error)
      throw error
    }
  }

  /**
   * Get recruiter metrics history
   * GET /api/metrics/recruiter/:userId
   */
  async getRecruiterMetrics(userId: string, limit: number = 12): Promise<RecruiterMetrics[]> {
    try {
      const response = await this.get<RecruiterMetrics[]>(`/api/metrics/recruiter/${userId}`, {
        params: {limit},
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching recruiter metrics:', userId, error)
      throw error
    }
  }

  // =============================================
  // Company Retention Endpoints
  // =============================================

  /**
   * Get company retention and health metrics
   * GET /api/metrics/companies/retention
   */
  async getCompanyRetention(startDate: Date, endDate: Date): Promise<CompanyRetentionResponse> {
    try {
      const response = await this.get<CompanyRetentionResponse>(
        '/api/metrics/companies/retention',
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company retention:', error)
      throw error
    }
  }

  // =============================================
  // Fill Metrics Endpoints
  // =============================================

  /**
   * Get fill ratio for a job order
   * GET /api/metrics/fill-ratio/:jobOrderId
   */
  async getFillRatio(jobOrderId: string, requestedCount: number): Promise<FillRatioResult> {
    try {
      const response = await this.get<FillRatioResult>(`/api/metrics/fill-ratio/${jobOrderId}`, {
        params: {requestedCount},
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching fill ratio:', jobOrderId, error)
      throw error
    }
  }

  /**
   * Get fill ratio by agency for a service order
   * GET /api/metrics/fill-ratio-by-agency/:serviceOrderId
   */
  async getFillRatioByAgency(serviceOrderId: string): Promise<FillRatioResult> {
    try {
      const response = await this.get<FillRatioResult>(
        `/api/metrics/fill-ratio-by-agency/${serviceOrderId}`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching fill ratio by agency:', serviceOrderId, error)
      throw error
    }
  }

  /**
   * Get quality fill ratio for a job order
   * GET /api/metrics/quality-fill-ratio/:jobOrderId
   */
  async getQualityFillRatio(
    jobOrderId: string,
    requestedCount: number,
  ): Promise<QualityFillRatioResult> {
    try {
      const response = await this.get<QualityFillRatioResult>(
        `/api/metrics/quality-fill-ratio/${jobOrderId}`,
        {
          params: {requestedCount},
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching quality fill ratio:', jobOrderId, error)
      throw error
    }
  }

  /**
   * Get quality fill ratio by agency for a period
   * GET /api/metrics/quality-fill-ratio-by-agency/:agencyId
   */
  async getQualityFillRatioByAgency(
    agencyId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<QualityFillRatioResult> {
    try {
      const response = await this.get<QualityFillRatioResult>(
        `/api/metrics/quality-fill-ratio-by-agency/${agencyId}`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching quality fill ratio by agency:', agencyId, error)
      throw error
    }
  }

  /**
   * Get fill velocity for an agency
   * GET /api/metrics/fill-velocity/:agencyId
   */
  async getFillVelocity(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    benchmark?: number,
  ): Promise<FillVelocityResult> {
    try {
      const response = await this.get<FillVelocityResult>(
        `/api/metrics/fill-velocity/${agencyId}`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(benchmark && {benchmark}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching fill velocity:', agencyId, error)
      throw error
    }
  }

  // =============================================
  // Attendance & Worker Endpoints
  // =============================================

  /**
   * Check if worker is suspended
   * GET /api/metrics/worker/:workerId/suspended
   */
  async isWorkerSuspended(workerId: string): Promise<WorkerSuspensionStatus> {
    try {
      const response = await this.get<WorkerSuspensionStatus>(
        `/api/metrics/worker/${workerId}/suspended`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error checking worker suspension:', workerId, error)
      throw error
    }
  }

  // =============================================
  // Company-Specific Metrics Endpoints
  // =============================================

  /**
   * Check if current user can access company metrics
   * GET /api/metrics/company/:companyId/access
   */
  async checkCompanyMetricsAccess(companyId: string): Promise<CompanyMetricsAccessResponse> {
    try {
      const response = await this.get<CompanyMetricsAccessResponse>(
        `/api/metrics/company/${companyId}/access`,
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error checking company metrics access:', companyId, error)
      throw error
    }
  }

  /**
   * Get company metrics overview (KPI dashboard)
   * GET /api/metrics/company/:companyId/overview
   */
  async getCompanyMetricsOverview(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyMetricsOverviewResponse> {
    try {
      const response = await this.get<CompanyMetricsOverviewResponse>(
        `/api/metrics/company/${companyId}/overview`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company metrics overview:', companyId, error)
      throw error
    }
  }

  /**
   * Get company financial metrics
   * GET /api/metrics/company/:companyId/financial
   */
  async getCompanyFinancialMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyFinancialMetricsResponse> {
    try {
      const response = await this.get<CompanyFinancialMetricsResponse>(
        `/api/metrics/company/${companyId}/financial`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company financial metrics:', companyId, error)
      throw error
    }
  }

  /**
   * Get company orders and fill performance metrics
   * GET /api/metrics/company/:companyId/orders
   */
  async getCompanyOrdersMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyOrdersMetricsResponse> {
    try {
      const response = await this.get<CompanyOrdersMetricsResponse>(
        `/api/metrics/company/${companyId}/orders`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company orders metrics:', companyId, error)
      throw error
    }
  }

  /**
   * Get company workforce analytics
   * GET /api/metrics/company/:companyId/workforce
   */
  async getCompanyWorkforceMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyWorkforceMetricsResponse> {
    try {
      const response = await this.get<CompanyWorkforceMetricsResponse>(
        `/api/metrics/company/${companyId}/workforce`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company workforce metrics:', companyId, error)
      throw error
    }
  }

  /**
   * Get company trends and analytics
   * GET /api/metrics/company/:companyId/trends
   */
  async getCompanyTrendsMetrics(
    companyId: string,
    startDate: Date,
    endDate: Date,
    agencyId?: string,
    siteId?: string,
  ): Promise<CompanyTrendsMetricsResponse> {
    try {
      const response = await this.get<CompanyTrendsMetricsResponse>(
        `/api/metrics/company/${companyId}/trends`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            ...(agencyId && {agencyId}),
            ...(siteId && {siteId}),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching company trends metrics:', companyId, error)
      throw error
    }
  }

  // =============================================
  // Agency Dashboard Endpoints
  // =============================================

  /**
   * Get agency dashboard summary with all KPIs
   * GET /api/metrics/agency/:agencyId/dashboard/summary
   */
  async getAgencyDashboardSummary(
    agencyId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<AgencyDashboardSummaryResponse> {
    try {
      const response = await this.get<AgencyDashboardSummaryResponse>(
        `/api/metrics/agency/${agencyId}/dashboard/summary`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency dashboard summary:', agencyId, error)
      throw error
    }
  }

  /**
   * Get agency recruiter performance and leaderboard
   * GET /api/metrics/agency/:agencyId/recruiters
   */
  async getAgencyRecruiters(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    limit: number = 20,
  ): Promise<AgencyRecruiterResponse> {
    try {
      const response = await this.get<AgencyRecruiterResponse>(
        `/api/metrics/agency/${agencyId}/recruiters`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit,
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency recruiters:', agencyId, error)
      throw error
    }
  }

  /**
   * Get agency client performance and health
   * GET /api/metrics/agency/:agencyId/clients
   */
  async getAgencyClients(
    agencyId: string,
    startDate: Date,
    endDate: Date,
    limit: number = 10,
  ): Promise<AgencyClientResponse> {
    try {
      const response = await this.get<AgencyClientResponse>(
        `/api/metrics/agency/${agencyId}/clients`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit,
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency clients:', agencyId, error)
      throw error
    }
  }

  /**
   * Get agency trends and analytics
   * GET /api/metrics/agency/:agencyId/trends
   */
  async getAgencyTrends(
    agencyId: string,
    startDate: Date,
    endDate: Date,
  ): Promise<AgencyTrendsResponse> {
    try {
      const response = await this.get<AgencyTrendsResponse>(
        `/api/metrics/agency/${agencyId}/trends`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
          },
        },
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching agency trends:', agencyId, error)
      throw error
    }
  }

  // =============================================
  // Utility Methods
  // =============================================

  /**
   * Get date range for common periods
   */
  static getDateRange(period: 'today' | 'week' | 'month' | 'quarter' | 'year'): DateRange {
    const now = new Date()
    const endDate = new Date(now)
    endDate.setHours(23, 59, 59, 999)

    let startDate: Date

    switch (period) {
      case 'today':
        startDate = new Date(now)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'week':
        startDate = new Date(now)
        startDate.setDate(now.getDate() - 7)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'month':
        startDate = new Date(now)
        startDate.setMonth(now.getMonth() - 1)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'quarter':
        startDate = new Date(now)
        startDate.setMonth(now.getMonth() - 3)
        startDate.setHours(0, 0, 0, 0)
        break
      case 'year':
        startDate = new Date(now)
        startDate.setFullYear(now.getFullYear() - 1)
        startDate.setHours(0, 0, 0, 0)
        break
      default:
        startDate = new Date(now)
        startDate.setMonth(now.getMonth() - 1)
        startDate.setHours(0, 0, 0, 0)
        break
    }

    return {startDate, endDate}
  }

  /**
   * Format a number based on the KPI format type
   */
  static formatKPIValue(value: number, format: DashboardKPI['format']): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value)
      case 'percent':
        return `${value.toFixed(1)}%`
      case 'hours':
        return `${value.toLocaleString()} hrs`
      case 'number':
      default:
        return value.toLocaleString()
    }
  }
}

export const metricsApi = new MetricsApi()
